import {
  AttachInternals,
  Build,
  Component,
  type ComponentInterface,
  Element,
  Event,
  type EventEmitter,
  Host,
  Listen,
  Prop,
  State,
  Watch,
  h,
} from '@stencil/core';
import type { DropdownOption } from '~/interfaces/IDropdown';
import { isElementInView, isScrollable, maintainScrollVisibility } from '~/util/DOM';

/**
 * @deprecated we have to use normal <select> element
 *
 * The file is excluded from the Biome check because the current implementation is not fully accessible.
 * The practice of reimplementing dropdowns and other elements that exist in the browser will always be flawed.
 */

/**
 * @part label - Change the dropdown label color, font size, etc.
 */
@Component({
  tag: 'ltc-dropdown',
  styleUrl: 'dropdown.less',
  shadow: { delegatesFocus: true },
  formAssociated: true,
})
export class Dropdown implements ComponentInterface {
  /**
   * 1. Own Properties
   */
  public static counter = 0;
  @AttachInternals() internals: ElementInternals;

  /**
   * 2. Reference to host HTML element.
   */
  @Element() hostElement: HTMLLtcDropdownElement;

  /**
   * 3. State() variables
   */
  @State() focusedOptionIndex = -1;

  @State() selectedOptionIndex = -1;

  @State() isOpen = false;

  /**
   * 4. Public Property API
   */
  /**
   * ID for labelledBy. Assigned automatically
   */
  @Prop({ reflect: true }) dropdownId: string | number = ++Dropdown.counter;

  /**
   * Sets the label for the dropdown
   */
  @Prop({ reflect: true }) label: string;

  /**
   * Sets the name of the select form control
   */
  @Prop({ reflect: true }) name: string;

  /**
   * Specify the default visible height of the options in the dropdown, in `px`
   */
  @Prop({ reflect: true }) maxHeight = 300;

  /**
   * The default placeholder option
   */
  @Prop({ reflect: true }) placeholder = 'Select';

  /**
   * Specify the label and value pairs for all dropdown options. Cannot be used as an attribute.
   */
  @Prop({ mutable: true, reflect: false }) options: DropdownOption[] = [];

  /**
   * Specify the label and value pairs for all dropdown options in `JSON.stringify(DropdownOption[])` format.
   * If present, will override `"options"` property.
   */
  @Prop({ reflect: true }) plainOptions: string;

  /**
   * Specify the color of the tiny "V" symbol on the dropdown
   */
  @Prop({ reflect: true }) dropdownIconColor = '#0069BA';

  /**
   * Specify the index of the initially selected option. Don't use with `"selected-value"`.
   */
  @Prop({ reflect: true }) selectedIndex = -1;

  /**
   * Specify the initially selected value. Don't use with `"selected-index"`.
   */
  @Prop({ reflect: true }) selectedValue: DropdownOption['value'];

  /**
   * Prop lifecycle events
   */
  @Watch('selectedIndex')
  onSelectedIndexChange(newValue: number) {
    const safeIndex = Math.max(-1, Math.min(newValue, this.options.length - 1));
    this.focusedOptionIndex = safeIndex;
    this.selectedOptionIndex = safeIndex;
  }

  @Watch('selectedValue')
  onSelectedValueChange(newValue: DropdownOption['value']) {
    const index = this.options.findIndex((o: DropdownOption) => o.value === newValue);
    this.focusedOptionIndex = index;
    this.selectedOptionIndex = index;
  }

  @Watch('plainOptions')
  onPlainOptionsChange(newValue: unknown) {
    if (newValue) {
      try {
        const options = JSON.parse(typeof newValue === 'string' ? newValue : JSON.stringify(newValue)) as DropdownOption[];
        if (options) {
          this.options = options;
        }
      } catch {
        // Do nothing
      }
    }
  }

  /**
   * 5. Events section
   */

  /**
   * This DOM event is fired when dropdown option is selected.
   */
  @Event() optionSelect: EventEmitter<DropdownOption['value']>;

  /**
   * 6. Component lifecycle events
   */
  constructor() {
    this.handleClickLabel = this.handleClickLabel.bind(this);
    this.handleClickToggle = this.handleClickToggle.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleSelectOption = this.handleSelectOption.bind(this);
  }

  componentWillLoad() {
    this.onPlainOptionsChange(this.plainOptions);

    if (this.selectedIndex >= 0) {
      this.onSelectedIndexChange(this.selectedIndex);
    } else if (this.selectedValue) {
      this.onSelectedValueChange(this.selectedValue);
    }
    this.updateFormValue(true);
  }

  /**
   * 7. Listeners
   */
  @Listen('click', { target: 'document' })
  handleClickOutside(event: MouseEvent) {
    const hostDOMRect = this.hostElement?.getBoundingClientRect();
    if (hostDOMRect) {
      const outHor = event.clientX <= hostDOMRect.left || event.clientX >= hostDOMRect.right;
      const outVert = event.clientY <= hostDOMRect.top || event.clientY >= hostDOMRect.bottom;

      if (outHor || outVert) {
        this.isOpen = false;
        this.focusedOptionIndex = this.selectedOptionIndex;
      }
    }
  }

  /**
   * 8. Public methods API
   */

  /**
   * 9. Local methods
   */
  toggleOptions(): void {
    this.isOpen = !this.isOpen;
  }

  handleClickToggle(event: MouseEvent): void {
    event.preventDefault();
    this.toggleOptions();
  }

  handleClickLabel(): void {
    (this.hostElement.shadowRoot?.querySelector('[role=combobox]') as HTMLElement)?.focus();
  }

  updateFormValue(silent = false): void {
    if (!Build.isServer) {
      const selectedOption = this.getSelectedOption();
      const formData = new FormData();
      formData.set(this.name, selectedOption.value.toString());
      this.internals.setFormValue(formData);
      if (!silent) {
        this.optionSelect.emit(selectedOption.value);
      }
    }
  }

  handleSelectOption(optionIndex: number): void {
    this.selectedOptionIndex = optionIndex;
    this.focusedOptionIndex = optionIndex;
    this.isOpen = false;
    this.updateFormValue();
  }

  scrollOptionIntoView(listBox: HTMLUListElement, activeOptionElement: HTMLLIElement) {
    if (!this.isOpen || this.options.length === 0) {
      return;
    }

    if (isScrollable(listBox)) {
      maintainScrollVisibility(activeOptionElement, listBox);
    }

    if (!isElementInView(activeOptionElement)) {
      activeOptionElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }

  handleKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.focusedOptionIndex = Math.min(this.focusedOptionIndex + 1, this.options.length - 1);
        this.isOpen = true;
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusedOptionIndex = Math.max(this.focusedOptionIndex - 1, 0);
        this.isOpen = true;
        break;
      case 'Enter':
        this.handleSelectOption(this.focusedOptionIndex);
        this.isOpen = false;
        break;
      case ' ':
        this.toggleOptions();
        break;
      default:
        this.isOpen = false;
        break;
    }

    const activeOptionElement = this.hostElement.shadowRoot
      ?.querySelectorAll('.dropdown--option-item')
      .item(this.focusedOptionIndex) as HTMLLIElement;
    const listBox = this.hostElement.shadowRoot?.querySelector('.dropdown--options') as HTMLUListElement;
    this.scrollOptionIntoView(listBox, activeOptionElement);
  }

  getPlaceholderOption(): DropdownOption {
    return {
      label: this.placeholder,
      value: '',
    };
  }

  getSelectedOption(): DropdownOption {
    if (this.selectedOptionIndex === -1 || this.options.length === 0) {
      return this.getPlaceholderOption();
    }

    return this.options[this.selectedOptionIndex];
  }

  /**
   * 10. render() function
   */
  render() {
    const ariaLabeledBy = this.label ? `dropdown_labelledby_${this.dropdownId}` : '';
    const listBoxID = `dropdown_listbox_${this.dropdownId}`;
    const selectedOption = this.getSelectedOption();
    return (
      <Host class={{ 'dropdown--is-open': this.isOpen }}>
        <div class="dropdown--root">
          {this.label && (
            // biome-ignore lint/a11y/noLabelWithoutControl: We need to rethink this implementation.
            <label
              class="dropdown--label"
              part="label"
              id={ariaLabeledBy}
              onClick={this.handleClickLabel}
              onKeyPress={this.handleClickLabel}
            >
              {this.label}
            </label>
          )}
          <input style={{ display: 'none' }} name={this.name} type="hidden" value={selectedOption.value} />
          <div
            aria-controls={listBoxID}
            aria-expanded={this.isOpen ? 'true' : 'false'}
            aria-haspopup="listbox"
            aria-labelledby={ariaLabeledBy}
            class="dropdown--current-selection"
            onClick={this.handleClickToggle}
            onKeyDown={this.handleKeyDown}
            // biome-ignore lint/a11y/useSemanticElements: We need to rethink this implementation.
            role="combobox"
            tabIndex={0}
          >
            <span>{selectedOption.label}</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
              <title>Dropdown Icon</title>
              <path
                d="M4.746 9.52 12.005 16.765C12.009 16.769 12.015 16.769 12.019 16.765L19.271 9.52"
                stroke-linecap="round"
                stroke-width="1.5"
                stroke={this.dropdownIconColor}
              />
            </svg>
          </div>
          {/* biome-ignore lint/a11y/useFocusableInteractive: We need to rethink this implementation. */}
          <div
            aria-labelledby={ariaLabeledBy}
            class="dropdown--options"
            id={listBoxID}
            // biome-ignore lint/a11y/useSemanticElements: We need to rethink this implementation.
            role="listbox"
            style={{ maxHeight: `${this.maxHeight}px` }}
            tabindex="-1"
          >
            {this.options.map((option: DropdownOption, index: number) => (
              // biome-ignore lint/a11y/useFocusableInteractive: We need to rethink this implementation.
              <div
                key={option.value}
                aria-selected={this.focusedOptionIndex === index ? 'true' : 'false'}
                class={{
                  'dropdown--option-item': true,
                  'dropdown--option-item-is-active': this.focusedOptionIndex === index,
                }}
                onClick={() => this.handleSelectOption(index)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    this.handleSelectOption(index);
                  }
                }}
                // biome-ignore lint/a11y/useSemanticElements: We need to rethink this implementation.
                role="option"
              >
                {option.label}
              </div>
            ))}
          </div>
        </div>
      </Host>
    );
  }
}
